import React, { useState, useEffect } from 'react';
import { X, RotateCcw, Save } from 'lucide-react';
import { ColumnDefinition } from '../../types/api';

interface ColumnPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  columnDefinitions: ColumnDefinition[];
  onSave: (preferences: Record<string, string>) => Promise<void>;
  onResetAll: () => Promise<void>;
  tableName: string;
}

const ColumnPreferencesModal: React.FC<ColumnPreferencesModalProps> = ({
  isOpen,
  onClose,
  columnDefinitions,
  onSave,
  onResetAll,
  tableName
}) => {
  const [editedColumns, setEditedColumns] = useState<Record<string, string>>({});
  const [resetColumns, setResetColumns] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Initialize edited columns when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialValues: Record<string, string> = {};
      columnDefinitions.forEach(col => {
        initialValues[col.key] = col.displayName;
      });
      setEditedColumns(initialValues);
      setResetColumns(new Set());
      setErrors({});
    }
  }, [isOpen, columnDefinitions]);

  const handleInputChange = (columnKey: string, value: string) => {
    setEditedColumns(prev => ({
      ...prev,
      [columnKey]: value
    }));

    // Remove from reset columns if user is editing it
    setResetColumns(prev => {
      const newSet = new Set(prev);
      newSet.delete(columnKey);
      return newSet;
    });

    // Clear error for this field
    if (errors[columnKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[columnKey];
        return newErrors;
      });
    }
  };

  const validateColumns = (): boolean => {
    const newErrors: Record<string, string> = {};
    const usedNames = new Set<string>();

    Object.entries(editedColumns).forEach(([key, name]) => {
      const trimmedName = name.trim();
      
      // Check empty
      if (!trimmedName) {
        newErrors[key] = 'Column name cannot be empty';
        return;
      }

      // Check length
      if (trimmedName.length > 200) {
        newErrors[key] = 'Column name is too long (max 200 characters)';
        return;
      }

      // Check duplicates
      const lowerName = trimmedName.toLowerCase();
      if (usedNames.has(lowerName)) {
        newErrors[key] = 'Duplicate column name';
        return;
      }
      usedNames.add(lowerName);
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateColumns()) {
      return;
    }

    setIsLoading(true);
    try {
      // Prepare preferences to send
      const changedPreferences: Record<string, string> = {};
      
      // Add custom preferences (non-default names)
      Object.entries(editedColumns).forEach(([key, name]) => {
        const original = columnDefinitions.find(col => col.key === key);
        if (original && name.trim() !== original.defaultName && !resetColumns.has(key)) {
          changedPreferences[key] = name.trim();
        }
      });
      
      // Add reset columns with empty string to delete them
      resetColumns.forEach(key => {
        changedPreferences[key] = '';
      });

      await onSave(changedPreferences);
      onClose();
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetAll = () => {
    setShowResetConfirm(true);
  };

  const confirmResetAll = async () => {
    setShowResetConfirm(false);
    setIsLoading(true);
    try {
      await onResetAll();
      onClose();
    } catch (error) {
      console.error('Failed to reset preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelResetAll = () => {
    setShowResetConfirm(false);
  };

  const handleResetSingle = (columnKey: string) => {
    const original = columnDefinitions.find(col => col.key === columnKey);
    if (original) {
      // Set to default name in UI
      setEditedColumns(prev => ({
        ...prev,
        [columnKey]: original.defaultName
      }));
      
      // Mark this column as reset so it gets deleted from backend
      setResetColumns(prev => new Set(prev).add(columnKey));
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Column Preferences - {tableName.charAt(0).toUpperCase() + tableName.slice(1)}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {columnDefinitions.map((column) => (
              <div key={column.key} className="flex items-center space-x-3">
                <div className="w-32 text-sm text-gray-600 truncate" title={column.defaultName}>
                  {column.defaultName}
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    value={editedColumns[column.key] || ''}
                    onChange={(e) => handleInputChange(column.key, e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors[column.key] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Custom column name..."
                  />
                  {errors[column.key] && (
                    <p className="mt-1 text-xs text-red-600">{errors[column.key]}</p>
                  )}
                </div>
                <button
                  onClick={() => handleResetSingle(column.key)}
                  className="p-2 text-gray-400 hover:text-orange-600"
                  title="Reset to default"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                {column.isCustomized && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full" title="Currently customized" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleResetAll}
              disabled={isLoading}
              className="flex items-center space-x-1 px-3 py-2 text-sm text-orange-600 hover:text-orange-800 disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Reset All</span>
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center space-x-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Reset All Confirmation Modal */}
      {showResetConfirm && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Reset All Column Names
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to reset all column names to their defaults? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelResetAll}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmResetAll}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md"
              >
                Reset All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnPreferencesModal;
